import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Configuration.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const Configuration = () => {
    const navigate = useNavigate();
    const [config, setConfig] = useState({
        granularity_minutes:30,
        dummy: false,
        time_limit: 30,
        mip_gap: 0.05,
        optimizer: "glpk"
    });

    const [originalConfig, setOriginalConfig] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/config`);
                if (!response.ok) {
                    throw new Error('Failed to fetch configuration');
                }
                const data = await response.json();
                const setupConfig = data.study.setup;
                setConfig(setupConfig);
                setOriginalConfig(setupConfig);
                setLoading(false);
            } catch (error) {
                setMessage({ type: 'error', text: error.message });
                setLoading(false);
            }
        };
        const storedUsername = sessionStorage.getItem('username');
        if (!storedUsername) {
            navigate('/');
            return;
        }
        
        // Set loading to true before fetching
        setLoading(true);
        // Actually call the fetchConfig function
        fetchConfig();
    }, [navigate]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // Handle different input types
        if (type === 'checkbox') {
            setConfig({ ...config, [name]: checked });
        } else if (type === 'number') {
            // Convert string values to numbers for number inputs
            const numValue = name === 'mip_gap' ? parseFloat(value) : parseInt(value);
            setConfig({ ...config, [name]: numValue });
        } else {
            setConfig({ ...config, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await fetch(`${API_BASE_URL}/config/setup`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(config)
            });

            if (!response.ok) {
                throw new Error('Failed to update configuration');
            }

            const result = await response.json();
            setOriginalConfig(config);
            setMessage({ 
                type: 'success', 
                text: 'Configuration updated successfully!' 
            });
            } catch (error) {
            console.error('Error updating configuration:', error);
            setMessage({ 
                type: 'error', 
                text: 'Failed to update configuration. Please try again.' 
            });
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = () => {
        return JSON.stringify(config) !== JSON.stringify(originalConfig);
    };

    const handleReset = () => {
        setConfig(originalConfig);
        setMessage({ type: '', text: '' });
    };

    if (loading) {
        return (
            <div className="config-container">
                <h1>Configuration Settings</h1>
                <div className="loading">Loading configuration...</div>
            </div>
        );
    }

     return (
        <div className="config-container">
            <h1>Configuration Settings</h1>
            
            {message.text && (
                <div className={`message ${message.type}`}>
                    {message.text}
                </div>
            )}
            
            <form onSubmit={handleSubmit} className="config-form">
                <div className="form-group">
                    <label htmlFor="granularity_minutes">Granularity (minutes)</label>
                    <input
                        type="number"
                        id="granularity_minutes"
                        name="granularity_minutes"
                        value={config.granularity_minutes}
                        onChange={handleInputChange}
                        min="1"
                        max="60"
                    />
                    <span className="help-text">Time step size in minutes (between 1-60)</span>
                </div>
                
                <div className="form-group">
                    <label htmlFor="time_limit">Time Limit (seconds)</label>
                    <input
                        type="number"
                        id="time_limit"
                        name="time_limit"
                        value={config.time_limit}
                        onChange={handleInputChange}
                        min="1"
                    />
                    <span className="help-text">Maximum computation time in seconds</span>
                </div>
                <div className="form-group">
                    <label htmlFor="mip_gap">MIP Gap</label>
                    <input
                        type="number"
                        id="mip_gap"
                        name="mip_gap"
                        value={config.mip_gap}
                        onChange={handleInputChange}
                        min="0"
                        max="1"
                        step="0.01"
                    />
                    <span className="help-text">Solver optimization gap (0-1)</span>
                </div>
                <div className="form-group">
                    <label htmlFor="optimizer">Optimizer</label>
                    <select
                        id="optimizer"
                        name="optimizer"
                        value={config.optimizer || "glpk"}
                        onChange={handleInputChange}
                    >
                        <option value="glpk">glpk</option>
                        <option value="gurobi">gurobi</option>
                    </select>
                    <span className="help-text">Select the optimization solver</span>
                </div>
                <div className="form-group checkbox-group">
                    <label htmlFor="dummy">
                        <input
                            type="checkbox"
                            id="dummy"
                            name="dummy"
                            checked={config.dummy}
                            onChange={handleInputChange}
                        />
                        Enable Dummy Mode
                    </label>
                    <span className="help-text">Use test data instead of real computations</span>
                </div>
                
                <div className="form-actions">
                    <button 
                        type="button" 
                        className="reset-button" 
                        onClick={handleReset}
                        disabled={!hasChanges() || saving}
                    >
                        Reset
                    </button>
                    <button 
                        type="submit" 
                        className="save-button" 
                        disabled={!hasChanges() || saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Configuration;